import * as angular from 'angular';
import * as _ from 'lodash';
import * as constants from '../../common/constants';

import { IConnectionPointService } from './connectionPoint.service';
import { IVariableTypesUploadResult, Qualifier, Subtype, VariableSubType, VariableType } from '../../models';
import { IRequestService, IUploadService } from '../../services';

export interface VariableTypeDto {
    variable: VariableType;
    url: string;
    tags: string;
}

export type ExtendedVariableType = VariableType & { subtypes: VariableSubType[]; qualifiers: Qualifier[] };

export interface IVariableService {
    getAll(): angular.IPromise<VariableTypeDto[]>;
    getAll(force: boolean): angular.IPromise<VariableTypeDto[]>;
    getAllExtended(): angular.IPromise<ExtendedVariableType[]>;
    getByGuid(guid: string): angular.IPromise<VariableType>;
    getList(guids: string[]): angular.IPromise<VariableTypeDto[]>;
    getForTagDefinition(tagDefinition): angular.IPromise<any>;
    getSubTypes(guid): angular.IPromise<VariableSubType[]>;
    getQualifiers(guid): angular.IPromise<Qualifier[]>;
    getAllSubtypes(): angular.IPromise<VariableSubType[]>;
    getAllSubtypes(force: boolean): angular.IPromise<VariableSubType[]>;
    getAllQualifiers(): angular.IPromise<Qualifier[]>;
    getAllQualifiers(force: boolean): angular.IPromise<Qualifier[]>;
    upload(file: File): angular.IPromise<IVariableTypesUploadResult>;
}

export class VariableService implements IVariableService {
    static serviceId = 'variableService';

    static $inject = [
        '$q',
        'requestService',
        'connectionPointService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private connectionPointService: IConnectionPointService,
        private _uploadService: IUploadService
    ) {
    }
    private getControllerUrl(): string {
        return this._requestService.getUrlV1() + 'variable';
    }

    getAll(force: boolean = false): angular.IPromise<VariableTypeDto[]> {
        const url = this.getControllerUrl();
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url);
    }

    getAllSubtypes(force: boolean = false): angular.IPromise<any> {
        const url = this.getControllerUrl() + '/subtypes';
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url);
    }

    getAllQualifiers(force: boolean = false): angular.IPromise<any> {
        const url = this.getControllerUrl() + '/qualifiers';
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url);
    }

    getAllExtended(): angular.IPromise<ExtendedVariableType[]> {
        const promises = [
            this.getAll(),
            this.getAllSubtypes(),
            this.getAllQualifiers(),
            this.connectionPointService.getAll()
        ];

        return this._$q.all(promises)
            .then(responses => {
                const variables = responses[0].map(v => v.variable);

                variables.forEach(v => {
                    v.subtypes = [];
                    v.qualifiers = [];

                    v.variableSubTypeGuidCollection.forEach(
                        guid => v.subtypes.push(_.find(responses[1], s => (<any>s).guid === guid))
                    );

                    v.qualifierCollection.forEach(
                        guid => v.qualifiers.push(_.find(responses[2], s => (<any>s).guid === guid))
                    );
                });
                return variables;
            });
    }

    getByGuid(guid: string): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'variable/' + guid;
        const defered = this._$q.defer();

        this._requestService.get(url).then((data: any) => {
            try {
                const variable: VariableType = data.variable;
                defered.resolve(variable);
            } catch (e) {
                console.error(e);
                defered.reject();
            }
        });

        return defered.promise;
    }

    getList(guids: string[]): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'variable/getList';
        const params = {
            guidList: guids
        };
        return this._requestService.get(url, params);
    }

    getForTagDefinition(tagDefinition): angular.IPromise<any> {
        const defered = this._$q.defer();
        const result = { type: '', unit: '', typeVariable: {} };

        this.getByGuid(tagDefinition.variableTypeGuid).then((variable) => {
            try {
                if (variable) {
                    result.typeVariable = variable;
                    result.type = variable.name;
                    if (tagDefinition.unitGuid !== constants.guidEmpty) {
                        const unit = _.find(variable.unitCollection, u => (<any>u).guid === tagDefinition.unitGuid);
                        if (unit) {
                            result.unit = (<any>unit).name;
                        }
                    }
                }
                defered.resolve(result);
            } catch (e) {
                console.error(e);
                defered.reject();
            }
        });

        return defered.promise;
    }

    getSubTypes(guid): angular.IPromise<Subtype[]> {
        const defered = this._$q.defer<Subtype[]>();
        const url = this._requestService.getUrlV1() + 'variable/subTypes/' + guid;

        this._requestService.get(url)
            .then((subtypes: Subtype[]) => {
                try {
                    if (subtypes) {
                        for (let i = 0; i < subtypes.length; i++) {
                            const subtype = subtypes[i];
                        }
                    } else {
                        subtypes = [
                            {
                                guid: constants.guidEmpty,
                                name: '',
                                longComment: ''
                            }
                        ];
                    }
                    defered.resolve(subtypes);
                } catch (e) {
                    console.error(e);
                    defered.reject();
                }
            });

        return defered.promise;
    }

    getQualifiers(guid): angular.IPromise<Qualifier[]> {
        const defered = this._$q.defer<Qualifier[]>();

        const url = this._requestService.getUrlV1() + 'variable/qualifiers/' + guid;
        this._requestService.get(url).then((qualifiers: Qualifier[]) => {
            try {
                if (!qualifiers) {
                    qualifiers = [
                        {
                            guid: constants.guidEmpty,
                            name: '',
                            longComment: ''
                        }
                    ];
                }
                defered.resolve(qualifiers);
            } catch (e) {
                console.error(e);
                defered.reject();
            }
        });

        return defered.promise;
    }

    upload(file: File): angular.IPromise<IVariableTypesUploadResult> {
        const url = `${this.getControllerUrl()}/upload`;
        return this._uploadService
            .uploadFile<IVariableTypesUploadResult>(url, file)
            .then(data => {
                this._requestService.cleanCacheAtV1('variable');
                this._requestService.cleanCacheAtV1('variable/subtypes');
                this._requestService.cleanCacheAtV1('variable/qualifiers');
                this._requestService.cleanCacheAtV1('variable/getList');
                this._requestService.cleanCacheAtV1('variable/subTypes/');
                this._requestService.cleanCacheAtV1('variable/qualifiers/');
                return data;
            });
    }
}